

import React, { useState, useEffect } from 'react';
import { useAppConfig, UpdateConfigStatus } from '../../../hooks/useAppConfig';
import { AppConfigParameter, AppConfig } from '../../../types';
import { SpinnerIcon, CheckCircleIcon, ExclamationTriangleIcon, SaveIcon } from '../../../components/Icons';
import { CIEN_CUADRAS_BUTTON_BLUE } from '../../../firebase/firebaseConfig';
import { InterestRatesConfig } from '../../../components/admin/InterestRatesConfig';

interface AdminAppConfigManagementProps {
  showOnly?: string[]; // Optional array of parameter IDs to show
}

export const AdminAppConfigManagement: React.FC<AdminAppConfigManagementProps> = ({ showOnly }) => {
  const { appConfig, configLoading, configError, updateAppConfigParameter, updateStatus } = useAppConfig();
  const [editableConfig, setEditableConfig] = useState<AppConfig>({});

  useEffect(() => {
    if (appConfig) {
      setEditableConfig(JSON.parse(JSON.stringify(appConfig))); // Deep copy to allow local edits
    }
  }, [appConfig]);

  const handleInputChange = (parameterId: string, field: keyof AppConfigParameter, value: any) => {
    setEditableConfig(prev => ({
      ...prev,
      [parameterId]: {
        ...(prev[parameterId] || { id: parameterId, name: parameterId }), // Ensure param object exists
        [field]: value,
      } as AppConfigParameter,
    }));
  };
  
  const handleNestedValueChange = (parameterId: string, path: string, value: string) => {
    const keys = path.split('.');
    const numericValue = parseFloat(value);
    if (isNaN(numericValue)) return;
    
    setEditableConfig(prev => {
        const newConfig = JSON.parse(JSON.stringify(prev));
        let current = newConfig[parameterId].value;
        for (let i = 0; i < keys.length - 1; i++) {
            current = current[keys[i]];
        }
        current[keys[keys.length - 1]] = numericValue;
        return newConfig;
    });
  };

  const handleSaveParameter = (parameterId: string) => {
    const originalParam = appConfig?.[parameterId];
    const editedParam = editableConfig[parameterId];

    if (!editedParam || !originalParam) return;

    const changes: Partial<AppConfigParameter> = {};
    if (editedParam.enabled !== undefined && editedParam.enabled !== originalParam.enabled) {
      changes.enabled = editedParam.enabled;
    }
    if (editedParam.current_version !== undefined && editedParam.current_version !== originalParam.current_version) {
      changes.current_version = editedParam.current_version;
    }
    if (editedParam.value !== undefined && JSON.stringify(editedParam.value) !== JSON.stringify(originalParam.value)) {
        changes.value = editedParam.value;
    }

    if (Object.keys(changes).length > 0) {
      updateAppConfigParameter(parameterId, changes);
    }
  };

  if (configLoading) {
    return (
      <section className="bg-white p-6 shadow-lg rounded-lg mt-6">
        <h3 className="text-lg font-semibold text-gray-700 mb-3">Configuración de la Aplicación</h3>
        <div className="flex items-center justify-center py-10">
          <SpinnerIcon className="w-10 h-10 text-sky-600 mr-3" />
          <span>Cargando configuración...</span>
        </div>
      </section>
    );
  }

  if (configError) {
    return (
      <section className="bg-white p-6 shadow-lg rounded-lg mt-6">
        <h3 className="text-lg font-semibold text-gray-700 mb-3">Configuración de la Aplicación</h3>
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
          <strong className="font-bold">Error:</strong>
          <span className="block sm:inline"> {configError}</span>
        </div>
      </section>
    );
  }

  if (!appConfig || Object.keys(appConfig).length === 0) {
    return (
      <section className="bg-white p-6 shadow-lg rounded-lg mt-6">
        <h3 className="text-lg font-semibold text-gray-700 mb-3">Configuración de la Aplicación</h3>
        <p className="text-gray-500 text-center py-6">No se encontraron parámetros de configuración. Asegúrese de que la colección 'app_config' exista en Firestore y contenga documentos.</p>
      </section>
    );
  }
  
  const renderParameterControl = (param: AppConfigParameter) => {
    const currentParamUpdateStatus: UpdateConfigStatus | undefined = updateStatus[param.id];
    const localParamValue = editableConfig[param.id] || param;

    switch (param.id) {
      case 'login_google_enabled':
      case 'login_email_password_enabled':
        return (
          <div className="flex items-center space-x-3">
            <label htmlFor={`param-${param.id}-enabled`} className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                id={`param-${param.id}-enabled`}
                checked={localParamValue.enabled === true}
                onChange={(e) => handleInputChange(param.id, 'enabled', e.target.checked)}
                className="form-checkbox h-5 w-5 text-sky-600 border-gray-300 rounded focus:ring-sky-500"
                disabled={currentParamUpdateStatus?.loading}
              />
              <span className="ml-2 text-sm text-gray-700">{localParamValue.enabled ? 'Habilitado' : 'Deshabilitado'}</span>
            </label>
          </div>
        );
      case 'app_version_info':
        return (
          <div className="flex items-center space-x-3">
            <input
              type="text"
              id={`param-${param.id}-version`}
              value={localParamValue.current_version || ''}
              onChange={(e) => handleInputChange(param.id, 'current_version', e.target.value)}
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm max-w-xs"
              placeholder="Ej: 1.0.0"
              disabled={currentParamUpdateStatus?.loading}
            />
          </div>
        );
      case 'interest_rates':
          return (
            <InterestRatesConfig 
              param={param}
              localParamValue={localParamValue}
              handleNestedValueChange={handleNestedValueChange}
              updateAppConfigParameter={updateAppConfigParameter}
              currentParamUpdateStatus={currentParamUpdateStatus}
            />
          );
      default:
        return <span className="text-sm text-gray-500">No editable</span>;
    }
  };

  const parametersToShow = [
    ...(appConfig ? Object.values(appConfig) : []),
    // Ensure 'interest_rates' is present in the list even if not in DB yet
    ...(!appConfig || !appConfig['interest_rates'] ? [{ id: 'interest_rates', name: 'Tasas de Interés (Simulador)', enabled: true } as AppConfigParameter] : [])
  ]
    .filter((param: AppConfigParameter) => !showOnly || showOnly.includes(param.id))
    .filter((param: AppConfigParameter, index: number, self: any[]) => self.findIndex(p => p.id === param.id) === index) // Remove duplicates
    .sort((a: AppConfigParameter, b: AppConfigParameter) => (a.name || a.id || '').localeCompare(b.name || b.id || ''));

  return (
    <section className="bg-white p-6 shadow-lg rounded-lg mt-6" aria-labelledby="app-config-management-heading">
      {!showOnly && (
        <>
            <h3 id="app-config-management-heading" className="text-lg font-semibold text-gray-700 mb-1">Configuración de la Aplicación</h3>
            <p className="text-sm text-gray-600 mb-6">
                Modifica los parámetros y flags de funcionalidades de la aplicación. Los cambios se aplican en tiempo real.
            </p>
        </>
      )}
      <div className="space-y-6">
        {parametersToShow.map((param: AppConfigParameter) => (
          <div key={param.id} className="p-4 border border-gray-200 rounded-md">
            <div className="flex flex-col sm:flex-row justify-between sm:items-center mb-2">
              <h4 className="text-md font-medium text-gray-800">{param.name} <span className="text-xs text-gray-500">({param.id})</span></h4>
              <div className="flex items-center mt-2 sm:mt-0">
                 <button
                  onClick={() => handleSaveParameter(param.id)}
                  disabled={
                    updateStatus[param.id]?.loading ||
                    JSON.stringify(editableConfig[param.id]) === JSON.stringify(appConfig[param.id]) // Disable if no changes
                  }
                  className="px-3 py-1.5 border border-transparent text-xs font-medium rounded-md shadow-sm text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sky-500 flex items-center justify-center space-x-1 disabled:opacity-50"
                  style={{ backgroundColor: CIEN_CUADRAS_BUTTON_BLUE }}
                  aria-label={`Guardar cambios para ${param.name}`}
                >
                  {updateStatus[param.id]?.loading ? (
                    <SpinnerIcon className="w-4 h-4" />
                  ) : (
                    <SaveIcon className="w-4 h-4" />
                  )}
                  <span className="ml-1">{updateStatus[param.id]?.loading ? 'Guardando...' : 'Guardar'}</span>
                </button>
                {updateStatus[param.id]?.success && (
                  <span className="ml-2 text-green-600 flex items-center text-xs">
                    <CheckCircleIcon className="w-4 h-4 mr-1" /> Guardado
                  </span>
                )}
                {updateStatus[param.id]?.error && (
                  <span className="ml-2 text-red-600 flex items-center text-xs" title={updateStatus[param.id]?.error || ''}>
                    <ExclamationTriangleIcon className="w-4 h-4 mr-1" /> Error
                  </span>
                )}
              </div>
            </div>
            {renderParameterControl(param)}
          </div>
        ))}
      </div>
    </section>
  );
};
